import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EMPTY_GUID } from '@core/constants';
import { AuthorizationDetailClinicalRationale, DenialRationaleRequest, DenialRationaleTemplate } from '@core/model';
import { LookupService } from '@core/service';
import { FeatureToggleService, SecurityService, noWhiteSpaceValidator, DateUtilities } from '@mynexus/mynexus-ui-lib';
import { DenialRationaleTemplatesEnum, OptionalRationaleTemplates } from '@shared/enumeration/denial-rationale-enum';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-denial-rationale',
    templateUrl: './denial-rationale.component.html',
    styleUrls: ['./denial-rationale.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DenialRationaleComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  private _edit = false;
  private _originalServiceLineId: string;

  @Input() model: AuthorizationDetailClinicalRationale;
  @Input() requestedAmount: number;
  @Input() approvedAmount: number;
  @Input() deniedAmount: number;
  @Input() isServiceLineFormInvalid: boolean;

  @Input() set edit(value: boolean) {
    this._edit = value;
    this.updateTemplateSelectorDisabledState();
  };

  get edit(): boolean {
    return this._edit;
  }

  @Input() req: DenialRationaleRequest;
  @Input() preview = false;
  @Input() currentCondition: string;
  @Input() currentPhysicianClinicalRationale: string;
  @Input() currentTemplateId: string;
  @Input() currentPhysicianNote: string;
  @Input() originalApprovedVisits: number;
  @Input() serviceLineId: string;
  
  @Output() updateCurrentData: EventEmitter<any> = new EventEmitter();
  @Output() save: EventEmitter<any> = new EventEmitter<any>();
  @Output() saveAndRouteToPDS: EventEmitter<any> = new EventEmitter<any>();
  @Output() cancel: EventEmitter<void> = new EventEmitter<void>();

  readonly emptyGuid = EMPTY_GUID;

  form: FormGroup;
  selectedTemplate: DenialRationaleTemplate = null;
  isTemplateInitialized = false;
  isEditingTemplate = false;
  isDenialRationalePilotTemplate = false;
  isLackOfPhysicianOrdersTemplate = false;
  condition: string;
  physicianClinicalRationale: string;
  physicianNote: string;
  endOfContenteditablePlaceholder = ' ';
  updateOnDestroy: boolean = true;
  denialRationaleTemplates: DenialRationaleTemplate[];
  denialRationaleTemplates$: Observable<DenialRationaleTemplate[]>;
  isDenialRationaleNewImplementationEnabled: boolean | null = null;
  
  get isInvalidForm(): boolean {
    if (!this.isDenialRationalePilotTemplate) {
      return !this.form.valid || this.isEditingTemplate;
    } else {
      if (this.model?.createdOn) {
        const currentDate = new Date();
        const createdOn = DateUtilities.convertUtcToLocal(this.model.createdOn);

        return !this.form.valid || ((currentDate.getTime() - createdOn.getTime()) / 86400000) > 30;
      } else {
        return !this.form.valid
      }
    }
  }

  get isNotOriginalServiceLine(): boolean {
   return this.serviceLineId !== this.form.controls['authorizationDetailId'].value;
  }

  @ViewChild('contenteditable') contenteditable!: ElementRef;

  private prevTemplate = '';

  constructor(
    private readonly ref: ChangeDetectorRef,
    private lookupService: LookupService,
    private securityService: SecurityService,
    private featureService: FeatureToggleService
  ) { }

  ngOnInit() {
    if (this.model && !this.model.templateLanguage) {
      this.model = null;
    }

    this.createForm(this.model);
    this.getDenialRationaleTemplates();

    if (!this.securityService.hasAccess(['ServiceLine.Write'])) {
      this.form.disable();
    }

    this.checkDenialRationalePilotTemplateName();
    this.isModifiedRationale();

    if (this.form && this.form.controls && this.form.controls.condition) this.form.controls.condition.setValue(this.currentCondition);
    if (this.form && this.form.controls && this.form.controls.physicianClinicalRationale) this.form.controls.physicianClinicalRationale.setValue(this.currentPhysicianClinicalRationale);
    if (this.form && this.form.controls && this.form.controls.physicianNote) this.form.controls.physicianNote.setValue(this.currentPhysicianNote);
    if (this.form && this.form.controls && this.form.controls.templateId) this.form.controls.templateId.setValue(this.currentTemplateId);
    if (this.form && this.form.controls && this.form.controls.authorizationDetailId) this.form.controls.authorizationDetailId.setValue(this._originalServiceLineId ?? this.serviceLineId);
  }

  ngOnChanges(changes: SimpleChanges): void
  {
    if (changes.serviceLineId && changes.serviceLineId.firstChange)
        this._originalServiceLineId = changes.serviceLineId.currentValue;
  }

  checkDenialRationalePilotTemplateName() {
    this.isDenialRationalePilotTemplate = this.form.get('templateDescr').value === DenialRationaleTemplatesEnum.DenialRationalePilot;
  }

  checkLackOfPhysicianOrderTemplateName() {
    this.isLackOfPhysicianOrdersTemplate = this.form.get('templateDescr').value === DenialRationaleTemplatesEnum.LackOfPhysicianOrders;
  }

  isModifiedRationale(): boolean {
    if (this.model?.createdOn && this.model?.modifiedOn) {
      const createOn = this.model.createdOn.replace(/\.\d+/, '');
      const modifiedOn = this.model.modifiedOn.replace(/\.\d+/, '');

      return createOn !== modifiedOn;
    } else {
      return false;
    }
  }

  ngOnDestroy() {
    if (this.updateOnDestroy && this.form && this.form.value && this.form.valid) this.updateCurrentData.emit([this.form.value.authorizationDetailId, this.form.value.condition, this.form.value.physicianClinicalRationale, this.form.value.templateId, this.form.value.physicianNote, false]);
  }

  getDenialRationaleTemplates(): void {
    this.featureService.getFeatureAccess('HH_Duals')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(enabled => {
        this.isDenialRationaleNewImplementationEnabled = enabled;

        this.updateCompleteRationaleMaxLength();

        if (this.isDenialRationaleNewImplementationEnabled) {
          this.getNewDenialRationaleTemplates();
        } else {
          this.getLegacyDenialRationaleTemplates();
        }

        if (!this.isDenialRationaleNewImplementationEnabled) {
          this.isTemplateInitialized = true;
        }
      });
  }

  private updateCompleteRationaleMaxLength(): void {
    const control = this.form.get('completeRationale');

    control.setValidators([Validators.maxLength(this.isDenialRationaleNewImplementationEnabled ? 5000 : 3000), noWhiteSpaceValidator]);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private getNewDenialRationaleTemplates(): void {
    if (!this.req && !this.edit) {
      return;
    }

    if (
      (this.approvedAmount === 0 && this.originalApprovedVisits > 0) ||  // SL visits have been changed from partially denied to fully denied
      (this.originalApprovedVisits === 0 && this.approvedAmount > 0)     // SL visits have been changed from fully denied to partially denied
    ) {
      this.model = null;
    }

    this.lookupService.getDenialRationaleTemplates(this.req)
      .pipe(
        map((res: AuthorizationDetailClinicalRationale[]) => res
          .filter(item =>
            (this.approvedAmount && item.denialType === 'Partially Favorable') ||
            (!this.approvedAmount && item.denialType === 'Denied') ||
            item.templateDescr === DenialRationaleTemplatesEnum.DenialRationalePilot
          )
          .map(item => ({
            ...item,
            templateLanguage: this.mapTemplate(item.templateLanguage)
          }))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((templates: AuthorizationDetailClinicalRationale[]) => {
        const template = this.model
          ? templates.find(item => item.templateId === this.model.templateId)
          : templates.find(item => item.templateDescr === DenialRationaleTemplatesEnum.DenialRationalePilot);

        if (!template) {
          this.model = null;

          return;
        }

        this.denialRationaleTemplates = templates;
        this.isTemplateInitialized = true;
        this.form.get('template').setValue(template);
        this.onSelectTemplate();
      });
  }

  private getLegacyDenialRationaleTemplates(): void {
    let request: Observable<AuthorizationDetailClinicalRationale[]>;
    if (this.req && this.edit) {
      if (
        (this.approvedAmount === 0 && this.originalApprovedVisits > 0) ||  // SL visits have been changed from partially denied to fully denied
        (this.originalApprovedVisits === 0 && this.approvedAmount > 0)     // SL visits have been changed from fully denied to partially denied
      ) {
        this.model = null;
      }
      request = this.lookupService.getDenialRationaleTemplates(this.req)
        .pipe(
          map((res: AuthorizationDetailClinicalRationale[]) => {
            res = res.filter(item => (this.approvedAmount && item.denialType === "Partially Favorable")
              || (!this.approvedAmount && item.denialType === "Denied")
              || (item.templateDescr === "Denial Rationale Pilot")
            )
            if (!this.model) {
              if (this.currentTemplateId && this.currentTemplateId.length) {
                const template = res.find(el => el.templateId === this.currentTemplateId);
                if (template) {
                  this.form.get('template').setValue(template);
                  this.form.get('templateLanguage').setValue(template.templateLanguage);
                  this.onSelectTemplate();
                }
              }
              return res;
            } else {
              const currentTemplate = {
                templateDescr: this.model.templateDescr,
                templateId: this.model.templateId,
                templateLanguage: this.model.templateLanguage,
              };
              let list = [];
              const isExistInList = res.some(el => el.templateId === this.model.templateId);
              if (isExistInList) {
                list = [
                  {
                    ...currentTemplate,
                    edited: true
                  },
                  ...res
                ];
              } else {
                list = [...res];
                this.model = null;
              }
              return list;
            }
          }));
    } else {
      request = of(this.model ? [{ ...this.model }] : []);
    }
    this.denialRationaleTemplates$ = request.pipe(
      map((res: AuthorizationDetailClinicalRationale[]) => {
        return res.map((item: AuthorizationDetailClinicalRationale) => {
          item.templateLanguage = this.mapTemplate(item.templateLanguage);
          return item;
        });
      }),
      tap((res: AuthorizationDetailClinicalRationale[]) => {
        if (this.model) {
          const template = res.find(el => el.templateId === this.model.templateId);
          this.form.get('template').setValue(template);
          this.form.get('templateLanguage').setValue(template.templateLanguage);
          this.onSelectTemplate();
        }
      })
    );
  }

  mapTemplate(template: string) {
    if (this.requestedAmount) {
      template = template.replace(/<SL Requested Visits>|\{\d+\}r/gi, `{${this.requestedAmount}}r`);
    }

    template = template.replace(/<SL Approved Visits>|\{\d+\}a/gi, `{${this.approvedAmount || 0}}a`);

    if (this.deniedAmount) {
      template = template.replace(/<SL Denied Visits>|\{\d+\}d/gi, `{${this.deniedAmount}}d`);
    }
    return template;
  }

  resetModel(): void {
    this.model = null;
  }

  watchTemplateParamsChanged() {
    if (this.isEditingTemplate) {
      const lastElementChild = this.contenteditable?.nativeElement.lastElementChild;
      const lastChild = this.contenteditable?.nativeElement.lastChild;
      if (lastElementChild === lastChild && lastElementChild.hasAttribute('contenteditable')) {
        const template = this.form.get('templateLanguage').value + ' ';
        this.form.get('templateLanguage').setValue(template);
      }
    }
    if (!this.isDenialRationalePilotTemplate) {
      const condition: string = this.form.get('condition').value || '';
      const physicianClinicalRationale: string = this.form.get('physicianClinicalRationale').value || '';
      let completeRationale = this.form.get('templateLanguage').value;

      if (condition.length) {
        completeRationale = completeRationale?.replace(
          '<Condition>',
          condition
        );
      }
      if (physicianClinicalRationale.length) {
        completeRationale = completeRationale?.replace(
          '<Physician Clinical Rationale>',
          physicianClinicalRationale
        );
      }

      // Remove tags for freezed text
      completeRationale = completeRationale && completeRationale.replace(/\{|\}[r|a|d]*/gi, '');
      this.form.get('completeRationale').setValue(completeRationale);
      this.ref.markForCheck();
    }
  }

  setFormWatcher(): void {
    this.form.get('templateLanguage').valueChanges.pipe().subscribe(this.watchTemplateParamsChanged.bind(this));
    this.form.get('condition').valueChanges.subscribe(this.watchTemplateParamsChanged.bind(this));
    this.form.get('physicianClinicalRationale').valueChanges.subscribe(this.watchTemplateParamsChanged.bind(this));
    this.form.get('physicianNote').valueChanges.subscribe(this.watchTemplateParamsChanged.bind(this));
  }

  createForm(model: AuthorizationDetailClinicalRationale) {
    this.form = new FormGroup({
      template: new FormControl({ value: null, disabled: !this.edit }),
      authorizationDetailClinicalRationaleId: new FormControl(null),
      authorizationDetailId: new FormControl(this.serviceLineId),
      authorizationNumber: new FormControl(null),
      templateId: new FormControl(null, Validators.required),
      templateDescr: new FormControl(null),
      physicianClinicalRationale: new FormControl('', Validators.maxLength(1250)),
      templateLanguage: new FormControl(''),
      completeRationale: new FormControl('', [Validators.maxLength(5000), noWhiteSpaceValidator]),
      physicianNote: new FormControl('', [Validators.maxLength(3000), noWhiteSpaceValidator]),
      condition: new FormControl('', Validators.maxLength(500)),
      rationaleType: new FormControl(null),
      sequenceNumber: new FormControl(null),
      isEditable: new FormControl(null),
      modifiedBy: new FormControl(null),
      modifiedOn: new FormControl(null),
      createdBy: new FormControl(null),
      createdOn: new FormControl(null),
    });

    if (model) {
      this.form.patchValue({
        ...model,
        templateLanguage: this.mapTemplate(model.templateLanguage)
      });
      this.watchTemplateParamsChanged();
    }

    if (!this.edit) {
      this.form.disable();
    }

    this.setFormWatcher();
  }

  onSelectTemplate() {
    const template = this.form.get('template').value;
    this.form.get('templateId').setValue(template.templateId);
    this.form.get('templateDescr').setValue(template.templateDescr);
    this.form.get('templateLanguage').setValue(this.mapTemplate(template.templateLanguage));
    this.checkDenialRationalePilotTemplateName();
    this.checkLackOfPhysicianOrderTemplateName();
    this.onChangeFormByDenialRationalePilot(this.form.get('templateDescr').value);
  }

  onChangeFormByDenialRationalePilot(templateName: string) {
    if (templateName === DenialRationaleTemplatesEnum.DenialRationalePilot) {
      this.form.controls["condition"].removeValidators(Validators.required);
      this.form.controls["condition"].updateValueAndValidity();
      this.form.controls["physicianClinicalRationale"].removeValidators(Validators.required);
      this.form.controls["physicianClinicalRationale"].updateValueAndValidity();

      this.form.controls["physicianNote"].addValidators(Validators.required);
      this.form.controls["completeRationale"].addValidators(Validators.required);
      this.form.controls["physicianNote"].updateValueAndValidity();
      this.form.controls["completeRationale"].updateValueAndValidity();
    } else if (templateName === DenialRationaleTemplatesEnum.LackOfPhysicianOrders) {
      this.form.controls["condition"].addValidators(Validators.required);
      this.form.controls["condition"].updateValueAndValidity();
      this.form.controls["physicianNote"].addValidators(Validators.required);
      this.form.controls["physicianNote"].updateValueAndValidity();
      this.form.controls["completeRationale"].addValidators(Validators.required);
      this.form.controls["completeRationale"].updateValueAndValidity();
      this.form.controls["physicianClinicalRationale"].removeValidators(Validators.required);
      this.form.controls["physicianClinicalRationale"].updateValueAndValidity();
    } else {
      this.form.controls["condition"].addValidators(Validators.required);
      this.form.controls["physicianClinicalRationale"].addValidators(Validators.required);
      this.form.controls["condition"].updateValueAndValidity();
      this.form.controls["physicianClinicalRationale"].updateValueAndValidity();

      this.form.controls["physicianNote"].removeValidators(Validators.required);
      this.form.controls["physicianNote"].updateValueAndValidity();
      this.form.controls["completeRationale"].removeValidators(Validators.required);
      this.form.controls["completeRationale"].updateValueAndValidity();
    }
    if (OptionalRationaleTemplates.has(templateName)) {
      this.form.controls["physicianClinicalRationale"].removeValidators(Validators.required);
      this.form.controls["physicianClinicalRationale"].updateValueAndValidity();
    }

  }

  private getTemplateForEdit(template: string): void {
    if (template.includes('<Condition>')) {
      this.condition = this.form.get('condition').value || '';
      this.condition = this.condition.replace(/\s{2,}/g, ' ');
      if (this.condition.length) {
        template = template?.replace(
          '<Condition>',
          `<span contenteditable="false" id="condition">${this.condition}</span>`
        );
      }
    }

    if (template.includes('<Physician Clinical Rationale>')) {
      this.physicianClinicalRationale = this.form.get('physicianClinicalRationale').value || '';
      this.physicianClinicalRationale = this.physicianClinicalRationale.replace(/\s{2,}/g, ' ');
      if (this.physicianClinicalRationale.length) {
        template = template?.replace(
          '<Physician Clinical Rationale>',
          `<span contenteditable="false" id="physician">${this.physicianClinicalRationale}</span>`
        );
      }
    }

    this.form.get('templateLanguage').setValue(this.mapTemplate(template));
  }

  private removeTags(): string {
    let template: string = this.form.get('templateLanguage').value;

    const spanWithCondition = `<span contenteditable="false" id="condition">${this.condition}</span>`
    if (template.includes(spanWithCondition)) {
      template = template?.replace(
        spanWithCondition,
        '<Condition>'
      );
    }

    const physicianClinicalRationale = `<span contenteditable="false" id="physician">${this.physicianClinicalRationale}</span>`
    if (template.includes(physicianClinicalRationale)) {
      template = template?.replace(
        physicianClinicalRationale,
        '<Physician Clinical Rationale>'
      );
    }

    return template;
  }

  onEditTemplate() {
    const template: string = this.form.get('templateLanguage').value;
    if (template) {
      this.prevTemplate = template;
      this.getTemplateForEdit(template);
      this.beginUpdateTemplate();
    }
  }

  onCancelEditedTemplate() {
    this.form.get('templateLanguage').setValue(this.prevTemplate);
    this.endUpdateTemplate();
  }

  onApplyEditedTemplate() {
    this.endUpdateTemplate();
    this.form.get('templateLanguage').setValue(this.removeTags());
  }

  onSave() {
    const model = this.Save();
    this.save.emit(model);
  }

  private Save() {
    const completeRationale = this.form.value.completeRationale;
    const templateLanguage = this.form.value.templateLanguage;
    const model = {
      ...this.form.value,
      templateLanguage: this.isDenialRationalePilotTemplate ? completeRationale : templateLanguage
    };

    this.updateCurrentData.emit([this.serviceLineId,  model.condition, model.physicianClinicalRationale, model.templateId, model.physicianNote,true]);

    this.updateOnDestroy = false;
    if (!model.authorizationDetailClinicalRationaleId) {
      model.authorizationDetailClinicalRationaleId = this.emptyGuid;
    }
    model.isEditable = true;
    delete model.template;
    this.form.reset();
    return model;
  }

  onSaveAndRouteToPDS(){
    const model = this.Save();
    this.saveAndRouteToPDS.emit(model);
  }

  onCancel() {
    this.endUpdateTemplate();
    this.form.reset();
    this.cancel.emit();
  }

  private updateTemplateSelectorDisabledState(): void {
    this.isEditingTemplate || !this.edit
      ? this.form?.get('template')?.disable()
      : this.form?.get('template')?.enable();
  }

  private beginUpdateTemplate(): void {
    this.isEditingTemplate = true;
    this.updateTemplateSelectorDisabledState();
  }

  private endUpdateTemplate(): void {
    this.isEditingTemplate = false;
    this.updateTemplateSelectorDisabledState();
  }
}
